import { useState, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { IndividualLicenseCheck } from 'api/common/constants'
import { trustdockBrowserSdk } from 'api/external/trustdock'
import { identificationRegisterForIndividualAPI } from 'api/identification'
import { getIndividualLicenseCheckMessage } from 'utilities/resources/options'

/**
 * 本人確認処理のカスタムフック
 * 
 * 【フック概要】
 * 本人確認（KYC）処理の共通ロジックを提供するカスタムフック
 * TrustDock SDKを使用した書類撮影から、APIでの検証、結果に応じた画面遷移まで一連の処理を管理
 * 
 * 【処理フロー】
 * 1. TrustDock Browser SDKを起動して本人確認書類を撮影
 * 2. 取得したverificationIdをバックエンドAPIに送信
 * 3. バックエンドでAI検証を実行
 * 4. 検証結果に応じて適切な画面に遷移
 *    - 成功: 呼び出し元で処理継続
 *    - 画質エラー: 品質エラー画面へ
 *    - BPO審査必要: BPO処理画面へ
 *    - その他: エラーメッセージ表示
 * 
 * 【リファクタリング内容（2025-07-22）】
 * - RegisterIdentificationForIndividualコンポーネントから共通ロジックを抽出
 * - 状態管理とエラーハンドリングを統一化
 * - 再利用可能なインターフェースを提供
 * - デバッグログを削除（本番環境のログ肥大化防止）
 * 
 * @returns {Object} 本人確認処理用のメソッドと状態
 * - startIdentification: 本人確認を開始する関数
 * - isProcessing: 処理中フラグ
 * - error: エラー情報
 * - clearError: エラーをクリアする関数
 */
export const useIdentification = () => {
  const navigate = useNavigate()
  const [isProcessing, setIsProcessing] = useState(false)
  const [error, setError] = useState<{ message: string } | null>(null)

  /**
   * 本人確認処理を開始する
   * 
   * @returns 成功時はtrue、失敗時はfalse
   */
  const startIdentification = useCallback(async (): Promise<boolean> => {
    setIsProcessing(true)
    setError(null)

    try {
      // TrustDock SDKを呼び出して検証IDを取得
      const verificationId = await trustdockBrowserSdk()
      
      if (!verificationId) {
        const message = getIndividualLicenseCheckMessage()
        setError({ message })
        return false
      }

      // APIを呼び出して結果を処理
      return new Promise((resolve) => {
        identificationRegisterForIndividualAPI(
          { verificationId }, 
          (result, shouldNavigateToBpo, errorMessage) => {
            // BPO審査が必要な場合
            if (shouldNavigateToBpo) {
              navigate('/bpo/processing', { replace: true })
              resolve(false)
              return
            }
            
            // 結果に応じた処理
            switch (result) {
              case IndividualLicenseCheck.Valid:
                // 成功
                resolve(true)
                return
                
              case IndividualLicenseCheck.ImageInvalid:
                // 画質エラー
                navigate('/identification/quality-error')
                resolve(false)
                return
                
              case IndividualLicenseCheck.BpoReview:
                // BPO審査
                navigate('/bpo/processing', { replace: true })
                resolve(false)
                return
                
              case IndividualLicenseCheck.NameInvalid:
              case IndividualLicenseCheck.BirthdayInvalid:
                // データ不一致（BPO審査へ移行するはず）
                resolve(false)
                return
                
              default:
                // その他のエラー
                const message = getIndividualLicenseCheckMessage(result)
                setError({ message })
                resolve(false)
            }
          }
        )
      })
    } catch (err) {
      setError({ message: '予期しないエラーが発生しました' })
      return false
    } finally {
      setIsProcessing(false)
    }
  }, [navigate])

  return {
    startIdentification,
    isProcessing,
    error,
    clearError: () => setError(null)
  }
}